import apiClient from "../api/apiClient";
import {
  clearSession,
  getSessionExpiry,
  setSessionExpiry,
} from "./sessionStore";

/**
 * The two session calls run on the shared `apiClient` like everything else.
 *
 * They used to be bare `axios.post`, because `apiClient` imported this module and the other
 * direction would have been a cycle. That cycle cost real money: bypassing the client also bypassed
 * its request interceptor, so when AUD-9 made `application-name` mandatory server-side, the
 * keep-alive and logout were the two requests in the app not sending it — every logged-in user.
 * `sessionStore` now holds the state both sides need, so nothing here is special any more.
 *
 * What IS special is the response handling, hence the opt-outs below.
 */
const sessionLifecycle = {
  // A 401 here means the session is already gone, which is not news to this module — `extend`
  // reports it by returning 0 and `logout` was ending the session anyway. Letting the interceptor
  // hard-redirect to "/" instead would race `InactivityHandler`, which signs out and then sends the
  // user to /login, and would cut the logout POST off before it was sent.
  skipUnauthorizedHandling: true,
  // Background lifecycle traffic. The user did not ask for this request and cannot act on its
  // failure; the visible consequence (the countdown ends, they are signed out) is already the
  // report. A modal saying "Request failed" over a session that just expired is noise.
  suppressErrorDialog: true,
} as const;

/**
 * Session lifecycle: the session token itself lives in an httpOnly cookie (`mtc_session`)
 * that the browser sends automatically with `withCredentials: true`. JS never sees or
 * stores the token — this defends against XSS exfiltration.
 *
 * What we still track in JS: the expiry timestamp, so we can drive the inactivity
 * countdown UI and decide whether the user is "signed in" without a network round-trip.
 * Expiry is held in localStorage (see `sessionStore`) so a page reload doesn't bounce the
 * user to /login.
 */
class SessionService {
  private static instance: SessionService;

  // Mirrored from the server (`/session/status`). The SERVER owns session lifetime; these exist
  // only so the UI can render a truthful countdown and the right warning. Never treat them as the
  // authority — an earlier design had the client running its own independent idle clock, which
  // drifted from the server's and produced silent logouts with no warning.
  //
  // Unlike the expiry these stay here, in memory: they are UI policy for the countdown, nobody
  // outside this module reads them, and `apiClient` has no use for them. Only what BOTH sides need
  // belongs in the store.
  private warningSeconds = 120;
  private canExtend = true;

  private constructor() {}

  public static getInstance(): SessionService {
    if (!SessionService.instance) {
      SessionService.instance = new SessionService();
    }
    return SessionService.instance;
  }

  /**
   * Whether the user has a (non-expired) session as far as the client can tell.
   * The server is still authoritative on every request via the cookie.
   */
  public isAuthenticated(): boolean {
    const expiry = getSessionExpiry();
    if (!expiry) return false;
    if (expiry < Date.now()) {
      this.clearSession();
      return false;
    }
    return true;
  }

  public getRemainingTime(): number {
    const expiry = getSessionExpiry();
    if (!expiry) {
      return 0;
    }
    return Math.max(0, expiry - Date.now());
  }

  /**
   * Record (or extend) the session expiry. Called after login, after MFA, and
   * after every heartbeat. The token itself is set as a cookie by the server;
   * we never see it.
   */
  public setSession(
    expiresIn: number,
    meta?: { canExtend?: boolean; warningSeconds?: number },
  ) {
    setSessionExpiry(Date.now() + expiresIn * 1000);
    if (meta?.canExtend !== undefined) this.canExtend = meta.canExtend;
    if (meta?.warningSeconds !== undefined) this.warningSeconds = meta.warningSeconds;
  }

  /** How long before expiry to warn the user (ms). Policy comes from the server. */
  public getWarningMs(): number {
    return this.warningSeconds * 1000;
  }

  /**
   * False once the session is up against its absolute lifetime cap — at that point extending
   * buys nothing and the user must sign in again. The warning dialog uses this to avoid
   * offering a "Stay signed in" button that cannot work.
   */
  public getCanExtend(): boolean {
    return this.canExtend;
  }

  /**
   * Tell the server the user is still present, and re-mirror the resulting expiry.
   * Called on genuine UI interaction (throttled) and from the "Stay signed in" button.
   * Returns the remaining time in ms (0 if the session is gone).
   */
  public async extend(): Promise<number> {
    try {
      const { data } = await apiClient.post(
        "/session/extend",
        {},
        sessionLifecycle,
      );
      this.setSession(data.expiresIn, {
        canExtend: data.canExtend,
        warningSeconds: data.warningSeconds,
      });
      return this.getRemainingTime();
    } catch {
      // The session is gone (expired / invalidated). Let the caller drive the logout.
      this.clearSession();
      return 0;
    }
  }

  /**
   * Kept as a method because half the app calls it through the singleton. The work itself lives in
   * `sessionStore`, which is also what `apiClient`'s 401 handler calls — one implementation, two
   * entry points, no cycle.
   */
  public clearSession() {
    clearSession();
  }

  /**
   * `keepTrustedDevice` is for the automatic sign-out after inactivity only: the session ends but
   * this browser keeps "Don't ask again on this device" — stepping away is not a decision to forget
   * the device (2026-10-20). Every sign-out the user chooses leaves it false, which forgets it.
   */
  public async logout({ keepTrustedDevice = false }: { keepTrustedDevice?: boolean } = {}) {
    try {
      await apiClient.post("/session/invalidate", { keepTrustedDevice }, sessionLifecycle);
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      this.clearSession();
      // Clear dialog sessionStorage on logout
      this.clearDialogSessionStorage();
    }
  }

  private clearDialogSessionStorage() {
    // Clear all dialog-related sessionStorage items
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith("whatWouldYouLikeToDoDialog_seen_")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  }
}

export default SessionService;
